import React, { useContext, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import SocialLoginButtons from '../components/SocialLoginButtons';
import { AlertCircle } from 'lucide-react';

const LoginPage = () => {
  const { user, login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const error = location.state?.error;

  useEffect(() => {
    // Handle redirect parameter from query string (e.g., from article polls)
    const redirectUrl = searchParams.get('redirect');
    
    if (user) {
      // User is already logged in, redirect to specified URL or home
      if (redirectUrl) {
        // Handle full URLs by extracting path
        try {
          const url = new URL(redirectUrl);
          navigate(url.pathname + url.search + url.hash);
        } catch {
          // If not a valid URL, use as path directly
          navigate(redirectUrl);
        }
      } else {
        navigate('/');
      }
    } else if (redirectUrl) {
      // User is not logged in but has a redirect URL - store it for after login
      // CRITICAL: Use localStorage to survive cross-domain auth redirect
      localStorage.setItem('authReturnUrl', redirectUrl);
    }
  }, [user, navigate, searchParams]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="login-page">
      <div className="max-w-xl w-full text-center">
        <img 
          src="https://customer-assets.emergentagent.com/job_b421cab0-afec-4d5c-8996-440a4a24e0cf/artifacts/fy51jndf_IMG_4650.jpeg" 
          alt="SheLovesThee Logo" 
          className="h-24 mx-auto mb-8"
          data-testid="login-logo"
        />
        
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-[#001f3f] mb-4 heading-font" data-testid="login-title">
          Welcome Back
        </h1>
        
        <p className="text-lg text-slate-700 mb-8">
          Login to access members-only content, make predictions, and join the conversation
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3 text-left">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <div>
              <p className="text-red-800 font-medium">Login Failed</p>
              <p className="text-red-600 text-sm">{error}</p>
              <p className="text-red-500 text-xs mt-1">Please try again. If this keeps happening, try clearing your browser cookies.</p>
            </div>
          </div>
        )}

        <div className="bg-white p-8 rounded-lg shadow-lg">
          <h2 className="text-xl font-bold uppercase heading-font text-[#001f3f] mb-6">
            Choose Your Sign In Method
          </h2>

          <SocialLoginButtons />

          <p className="text-xs text-slate-500 mt-6">
            By signing in, you agree to our <a href="/terms" className="text-[#007AFF] hover:underline">Terms of Service</a> and <a href="/privacy" className="text-[#007AFF] hover:underline">Privacy Policy</a>
          </p>
        </div>

        <div className="mt-8 text-sm text-slate-600">
          <p>New here? No problem!</p>
          <p className="font-bold text-[#007AFF]">Membership is 100% FREE</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
